import { useStore, type Screen } from '../store';
import { VTile } from './VMark';
import { isProgrammeAdmin } from '../auth/cognito';
import { isSampleMode } from '../api/client';
import { ProfileMenu } from './ProfileMenu';

// Tab order follows the design: participant journey first, the admin surface last.
// The 1st tab shows "Dashboard" (key `welcome`); the 5th shows "Organisations" (admin-only),
// which replaced the separate Campaign + Budget screens. My-assessments is merged into the
// Dashboard. The Organisations tab stays highlighted on the per-org detail screen.
const TABS: { key: Screen; label: string; adminOnly?: boolean }[] = [
  { key: 'welcome', label: 'Dashboard' },
  { key: 'assessment', label: 'Assessment' },
  { key: 'practice', label: 'Practice' },
  { key: 'templates', label: 'Templates' },
  { key: 'report', label: 'Report' },
  { key: 'organisations', label: 'Organisations', adminOnly: true },
  { key: 'settings', label: 'Settings', adminOnly: true },
];

// The Organisations surface is for programme admins only. Sample mode shows it so the demo
// is complete; live mode shows it only on an admin claim.
function visibleTabs() {
  const showAdmin = isSampleMode || isProgrammeAdmin();
  return TABS.filter((t) => !t.adminOnly || showAdmin);
}

/** Persistent product nav (Code Black, 60px) on every screen. */
function initials(name: string): string {
  // An email address or the neutral fallback is not a full name.
  if (name.includes('@') || name === 'You') return '';
  const words = name.trim().split(/\s+/).filter(Boolean);
  return (
    (words.length > 1 ? [words[0], words[words.length - 1]] : words)
      .map((w) => w[0])
      .filter(Boolean)
      .join('')
      .toUpperCase()
  );
}

export function Chrome() {
  const screen = useStore((s) => s.screen);
  const setScreen = useStore((s) => s.setScreen);
  const openAssessment = useStore((s) => s.openAssessment);
  // The signed-in user's display name. The "Jordan Mercer" demo persona is only a fallback
  // in sample mode (no auth); a live, signed-in user shows their own identity (or a neutral
  // placeholder if the token carries no name/email yet) — never the demo persona.
  const userName = useStore((s) => s.userName) ?? (isSampleMode ? 'Jordan Mercer' : 'You');
  const productName = useStore((s) => s.productName);

  return (
    <header
      className="sticky top-0 z-40 flex h-[60px] items-center justify-between bg-code px-4 text-white print:hidden"
      style={{ borderBottom: '1px solid #26262E' }}
    >
      <button
        type="button"
        onClick={() => setScreen('welcome')}
        className="flex items-center gap-2.5"
        aria-label={`${productName} home`}
      >
        <VTile size={30} bg="#00D700" v="#000000" />
        <div className="text-left leading-none">
          <div className="text-[16px] font-bold tracking-[-0.02em]">{productName}</div>
          <div className="text-[9.5px] font-semibold tracking-[0.18em] text-storm">BY VERSENT</div>
        </div>
      </button>

      <nav
        aria-label="Surfaces"
        className="hidden items-center gap-1 rounded-lg p-1 sm:flex"
        style={{ background: '#121218', border: '1px solid #26262E' }}
      >
        {visibleTabs().map((t) => {
          // Keep Organisations highlighted while drilled into a single org's detail screen.
          const active = screen === t.key || (t.key === 'organisations' && screen === 'orgDetail');
          return (
            <button
              key={t.key}
              onClick={() => (t.key === 'assessment' ? openAssessment() : setScreen(t.key))}
              aria-current={active ? 'page' : undefined}
              className={`rounded-ctl px-3.5 py-[7px] text-[13px] font-semibold transition-colors duration-150 ${
                active
                  ? 'bg-green text-code'
                  : 'bg-transparent text-[#9A9AA4] hover:text-white'
              }`}
            >
              {t.label}
            </button>
          );
        })}
      </nav>

      <ProfileMenu name={userName} initials={initials(userName)} />
    </header>
  );
}
